<template>
  <transition name="fade" @before-enter="beforeEnter" @leave="leave">
    <div v-if="isVisible" :class="['info-bar-container', props.type, { grid: resolvedViewType === DataViewType.Grid }, { fullWidth: props.fullWidth }]">
      <div class="message_container">
        <IconWrapper :name="iconName" />
        <div class="message">
          <slot name="message">{{ props.message }}</slot>
        </div>
      </div>
      <div v-if="props.closeable" class="message_container close" @click="close">
        <IconWrapper name="closeIconGray" class="close-icon" />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { DataViewType } from '@/interfaces'
import { useViewType } from '@/composables'
import { ref, computed } from 'vue'
import { onBeforeMount } from 'vue'
import { getCookie, setCookie } from '@/services/utils'
import { useRouter } from 'vue-router'
import IconWrapper from '@/components/common/icons/IconWrapper.vue'
import { InfoBarIconNames, InfoBarTypes } from './types'

const { viewType: cachedViewType } = useViewType()
const router = useRouter()
const currentRoute = router.currentRoute.value.name

interface InfoBarProps {
  type: InfoBarTypes
  message: string
  closeable?: boolean
  customContainerClass?: string
  withCookie?: boolean
  fullWidth?: boolean
  viewType?: DataViewType
}

interface InfoBarEmits {
  (event: 'onInfoBarToggle', isVisible: boolean): void
}

const emits = defineEmits<InfoBarEmits>()
const props = withDefaults(defineProps<InfoBarProps>(), {
  closeable: true,
  withCookie: true,
  fullWidth: false
})
const isVisible = ref(false)
const resolvedViewType = computed(() => props.viewType ?? cachedViewType.value)
const iconName = computed(() => {
  const iconsbyType = {
    [InfoBarTypes.Warning]: InfoBarIconNames.Warning,
    [InfoBarTypes.Danger]: InfoBarIconNames.Danger,
    [InfoBarTypes.Info]: InfoBarIconNames.Info,
    [InfoBarTypes.Notification]: InfoBarIconNames.Notification,
    [InfoBarTypes.Success]: InfoBarIconNames.Success
  }
  return iconsbyType[props.type] || InfoBarIconNames.Notification
})
const beforeEnter = (el: HTMLElement) => {
  el.style.opacity = '0'
}
// eslint-disable-next-line @typescript-eslint/ban-types
const leave = (el: HTMLElement, done: Function) => {
  el.style.transition = 'opacity 0.5s'
  el.style.opacity = '0'
  setTimeout(() => {
    done()
  }, 500)
}

function close() {
  isVisible.value = false
  emits('onInfoBarToggle', isVisible.value)
  if (!props.withCookie) return
  setCookie(`infobar_${String(currentRoute)}`, 'closed', 365)
}

onBeforeMount(() => {
  if (!props.withCookie) {
    isVisible.value = true
  } else {
    isVisible.value = getCookie(`infobar_${String(currentRoute)}`) !== 'closed'
  }
  emits('onInfoBarToggle', isVisible.value)
})
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.info-bar-container {
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  border-radius: 6px;
  width: 100%;
  align-items: center;
  margin-bottom: 12px;

  img {
    width: 24px;
    height: 24px;
  }

  .message_container {
    display: flex;
    align-items: center;

    .icon {
      margin-right: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .close-icon {
      margin: 0;
      width: 16px;
      height: 16px;
      margin-inline-start: 10px;
    }

    .message {
      color: $bodyText;
      font-size: 0.875rem;
      letter-spacing: 0.1px;
    }
  }

  .close {
    cursor: pointer;
    filter: none;
    transition: filter 0.4s;
    &:hover {
      filter: brightness(0) saturate(100%) invert(36%) sepia(32%) saturate(373%) hue-rotate(201deg) brightness(100%) contrast(91%);
    }
  }

  &.grid {
    width: calc(100% - 75px);
    left: 35px;
    position: relative;
  }

  &.fullWidth {
    width: 100%;
    left: 0;
  }
}


/* Base Styles */
.notification {
  border: 1px solid #000;
}

/* Styles for Warning */
.warning {
  background: #fff9e9;
  border-color: rgba(255, 198, 50, 0.1);
}

/* Styles for Info */
.info {
  background: $info-blue-background;
  border-color: $info-blue-border;
}

/* Styles for Danger */
.danger {
  background: #fdedee;
}

.success {
  background: #eefcf6;
  border: 1px solid #dcf9ee;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<!-- COMPONENT USAGE
<InfoBar  type="warning" message="your message"/>
<InfoBar  type="info" message="your message"/>
<InfoBar  type="notification" />
<InfoBar  type="danger" message="your message"/>
ADD CUSTOM MESSAGES BY USING THE MESSAGE PROP
-->
